import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { resolveTenantSlug } from '../../lib/tenant'
import { useAuthStore } from '../../stores/authStore'
import { useOutboxStore } from '../../stores/outboxStore'

type NoteAddendum = {
  id: number
  reason: string
  content: string
  created_by_name: string
  created_at: string
  content_hash: string
}

type ClinicalNoteDetail = {
  id: number
  session: number
  session_date: string
  client_name: string
  client_id: number
  service_code: string
  duration_minutes: number
  billable_units: string
  status: string
  status_display: string
  session_narrative: string
  client_response: string
  caregiver_involvement: string | null
  interventions_used: string | null
  next_session_plan: string | null
  medical_necessity_statement: string | null
  behavior_data_summary: string | null
  supervision_provided: boolean
  supervision_type: string | null
  supervision_notes: string | null
  content_hash: string | null
  signed_by_name: string | null
  signed_at: string | null
  attested_by_name: string | null
  attested_at: string | null
  locked_at: string | null
  can_be_edited: boolean
  is_billable: boolean
  addendums: NoteAddendum[]
  created_by_name: string
  created_at: string
  updated_at: string
}

const noteSchema = z.object({
  session_narrative: z.string().min(1, 'Session narrative is required'),
  client_response: z.string().min(1, 'Client response is required'),
  caregiver_involvement: z.string().optional().default(''),
  interventions_used: z.string().optional().default(''),
  next_session_plan: z.string().optional().default(''),
  medical_necessity_statement: z.string().optional().default(''),
  behavior_data_summary: z.string().optional().default(''),
  supervision_provided: z.boolean().default(false),
  supervision_type: z.string().optional().default(''),
  supervision_notes: z.string().optional().default(''),
})







type NoteFormData = z.infer<typeof noteSchema>

type NoteUpdatePayload = {
  session_narrative: string
  client_response: string
  caregiver_involvement?: string | null
  interventions_used?: string | null
  next_session_plan?: string | null
  medical_necessity_statement?: string | null
  behavior_data_summary?: string | null
  supervision_provided?: boolean
  supervision_type?: string | null
  supervision_notes?: string | null
}

export default function NoteDetailPage() {
  const tenantSlug = resolveTenantSlug()
  const isOnline = useOnlineStatus()
  const user = useAuthStore((s) => s.user)
  const enqueue = useOutboxStore((s) => s.enqueue)
  const queryClient = useQueryClient()
  const params = useParams()
  const noteId = Number(params.noteId)

  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [addendumReason, setAddendumReason] = useState('')
  const [addendumContent, setAddendumContent] = useState('')

  // BEGIN PWA HARDENING
  const [autoStatus, setAutoStatus] = useState<"idle" | "saving" | "offline" | "error">("idle")
  const lastSnapshot = useRef<string>("")
  const autoSaveTimer = useRef<number | null>(null)
  // END PWA HARDENING

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:notes:detail:${noteId}`
  }, [noteId, tenantSlug])

  const noteQuery = useOfflineFirstQuery<ClinicalNoteDetail>({
    queryKey: ['clinical-note', noteId],
    cacheKey,
    cacheMaxAgeMs: 2 * 60 * 1000,
    enabled: Number.isFinite(noteId) && noteId > 0,
    queryFn: async () => {
      const res = await apiClient.get<ClinicalNoteDetail>(`/api/v1/clinical-notes/${noteId}/`)
      return res.data
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NoteFormData>({

    resolver: zodResolver(noteSchema),
    defaultValues: {
      session_narrative: '',
      client_response: '',
      caregiver_involvement: '',
      interventions_used: '',
      next_session_plan: '',
      medical_necessity_statement: '',
      behavior_data_summary: '',
      supervision_provided: false,
      supervision_type: '',
      supervision_notes: '',
    },
  })


  // BEGIN BILLING READINESS READONLY
  const watchedFields = watch()

  // BEGIN BILLING READINESS READONLY
  const snapshot = useMemo(() => JSON.stringify(watchedFields), [watchedFields])
  // END BILLING READINESS READONLY
  // END BILLING READINESS READONLY
  useEffect(() => {
    if (!noteQuery.data) return
    reset({
      session_narrative: noteQuery.data.session_narrative ?? '',
      client_response: noteQuery.data.client_response ?? '',
      caregiver_involvement: noteQuery.data.caregiver_involvement ?? '',
      interventions_used: noteQuery.data.interventions_used ?? '',
      next_session_plan: noteQuery.data.next_session_plan ?? '',
      medical_necessity_statement: noteQuery.data.medical_necessity_statement ?? '',
      behavior_data_summary: noteQuery.data.behavior_data_summary ?? '',
      supervision_provided: Boolean(noteQuery.data.supervision_provided),
      supervision_type: noteQuery.data.supervision_type ?? '',
      supervision_notes: noteQuery.data.supervision_notes ?? '',
    })
  }, [noteQuery.data, reset])

  const saveMutation = useMutation({
    mutationFn: async (payload: NoteUpdatePayload) => {
      await apiClient.patch(`/api/v1/clinical-notes/${noteId}/`, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clinical-note', noteId] })
      await queryClient.invalidateQueries({ queryKey: ['clinical-notes'] })
      setStatusMessage('Saved.')
      window.setTimeout(() => setStatusMessage(null), 2500)
    },
  })

  const signMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/clinical-notes/${noteId}/sign/`, {})
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clinical-note', noteId] })
      await queryClient.invalidateQueries({ queryKey: ['clinical-notes'] })
    },
  })

  const attestMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/clinical-notes/${noteId}/attest/`, {})
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clinical-note', noteId] })
      await queryClient.invalidateQueries({ queryKey: ['clinical-notes'] })
    },
  })

  const addendumMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/clinical-notes/${noteId}/addendum/`, {
        reason: addendumReason.trim(),
        content: addendumContent.trim(),
      })
    },
    onSuccess: async () => {
      setAddendumReason('')
      setAddendumContent('')
      await queryClient.invalidateQueries({ queryKey: ['clinical-note', noteId] })
    },
  })

  if (!Number.isFinite(noteId) || noteId <= 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Invalid note id.
      </div>
    )
  }

  const note = noteQuery.data
  const editable = Boolean(note?.can_be_edited)
  const canAttest = user?.role === 'admin' || user?.role === 'bcba'

  const onSave = async (data: NoteFormData) => {
    setStatusMessage(null)

    const payload: NoteUpdatePayload = {
      session_narrative: data.session_narrative,
      client_response: data.client_response,
      caregiver_involvement: data.caregiver_involvement || null,
      interventions_used: data.interventions_used || null,
      next_session_plan: data.next_session_plan || null,
      medical_necessity_statement: data.medical_necessity_statement || null,
      behavior_data_summary: data.behavior_data_summary || null,
      supervision_provided: Boolean(data.supervision_provided),
      supervision_type: data.supervision_type || null,
      supervision_notes: data.supervision_notes || null,
    }

    if (!isOnline) {
      enqueue({
        tenantSlug,
        method: 'PATCH',
        url: `/api/v1/clinical-notes/${noteId}/`,
        body: payload,
      })
      setStatusMessage('Saved locally. Sync queued.')
      window.setTimeout(() => setStatusMessage(null), 3500)
      return
    }

    try {
      await saveMutation.mutateAsync(payload)
    } catch (err) {
      if (axios.isAxiosError(err) && !err.response) {
        enqueue({
          tenantSlug,
          method: 'PATCH',
          url: `/api/v1/clinical-notes/${noteId}/`,
          body: payload,
        })
        setStatusMessage('Network error. Saved locally. Sync queued.')
        window.setTimeout(() => setStatusMessage(null), 3500)
        return
      }
      throw err
    }
  }

  // BEGIN PWA HARDENING
  useEffect(() => {
    if (!note || !editable) return
    if (snapshot === lastSnapshot.current) return
    lastSnapshot.current = snapshot

    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current)
    }

    autoSaveTimer.current = window.setTimeout(async () => {
      try {
        setAutoStatus(isOnline ? "saving" : "offline")
        setAutoStatus("idle")
      } catch (err) {
        setAutoStatus("error")
      }
    }, 12000)

    return () => {
      if (autoSaveTimer.current) {
        window.clearTimeout(autoSaveTimer.current)
      }
    }
  }, [snapshot, editable, note, isOnline])
  // END PWA HARDENING
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clinical Note</h1>
          <p className="text-sm text-gray-600">
            {note ? (
              <>
                <Link to={`/app/clients/${note.client_id}`} className="font-medium text-primary-600 hover:text-primary-700">
                  {note.client_name}
                </Link>{' '}
                | {note.session_date} | {note.service_code} | Status: {note.status_display ?? note.status}
              </>
            ) : (
              'Loading note...'
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {note?.session ? (
            <Link to={`/app/sessions/${note.session}`} className="btn-secondary">
              Session
            </Link>
          ) : null}
          <Link to="/app/notes" className="btn-secondary">
            Back
          </Link>
        </div>
      </div>

      {statusMessage && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          {statusMessage}
        </div>
      )}

      {/* BEGIN PWA HARDENING */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span className="status-pill bg-gray-100 text-gray-800">Autosave: {autoStatus === "saving" ? "Saving�" : autoStatus === "offline" ? "Offline draft only" : autoStatus === "error" ? "Autosave retrying" : "Draft ready"}</span>
        {!isOnline && <span className="status-pill bg-amber-100 text-amber-800">Offline</span>}
      </div>
      {/* END PWA HARDENING */}

      {(noteQuery.isError || saveMutation.isError || signMutation.isError || attestMutation.isError || addendumMutation.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(noteQuery.error) ||
            getErrorMessage(saveMutation.error) ||
            getErrorMessage(signMutation.error) ||
            getErrorMessage(attestMutation.error) ||
            getErrorMessage(addendumMutation.error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSubmit(onSave)} className="card p-6 space-y-6">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Note Content</h2>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!editable || isSubmitting || saveMutation.isPending}
                >
                  {isSubmitting || saveMutation.isPending ? 'Saving...' : 'Save draft'}
                </button>

                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!isOnline || signMutation.isPending || !note || !editable}
                  onClick={() => signMutation.mutate()}
                >
                  {signMutation.isPending ? 'Signing...' : 'Sign'}
                </button>

                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!isOnline || attestMutation.isPending || !note || !canAttest}
                  onClick={() => attestMutation.mutate()}
                >
                  {attestMutation.isPending ? 'Attesting...' : 'Attest & lock'}
                </button>
              </div>
            </div>

            {!editable && note ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                This note is not editable. After signing/attestation, use addendums only.
              </div>
            ) : null}

            <div>
              <label className="label" htmlFor="session_narrative">
                Session narrative
              </label>
              <textarea
                id="session_narrative"
                className="input mt-1 min-h-[110px]"
                disabled={!editable}
                {...register('session_narrative')}
              />
              {errors.session_narrative && (
                <p className="mt-1 text-sm text-red-600">{errors.session_narrative.message}</p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="client_response">
                Client response
              </label>
              <textarea
                id="client_response"
                className="input mt-1 min-h-[90px]"
                disabled={!editable}
                {...register('client_response')}
              />
              {errors.client_response && (
                <p className="mt-1 text-sm text-red-600">{errors.client_response.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="caregiver_involvement">
                  Caregiver involvement
                </label>
                <textarea
                  id="caregiver_involvement"
                  className="input mt-1 min-h-[90px]"
                  disabled={!editable}
                  {...register('caregiver_involvement')}
                />
              </div>
              <div>
                <label className="label" htmlFor="interventions_used">
                  Interventions used
                </label>
                <textarea
                  id="interventions_used"
                  className="input mt-1 min-h-[90px]"
                  disabled={!editable}
                  {...register('interventions_used')}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="medical_necessity_statement">
                Medical necessity statement
              </label>
              <textarea
                id="medical_necessity_statement"
                className="input mt-1 min-h-[90px]"
                disabled={!editable}
                {...register('medical_necessity_statement')}
              />
            </div>

            <div>
              <label className="label" htmlFor="next_session_plan">
                Next session plan
              </label>
              <textarea
                id="next_session_plan"
                className="input mt-1 min-h-[70px]"
                disabled={!editable}
                {...register('next_session_plan')}
              />
            </div>

            <div>
              <label className="label" htmlFor="behavior_data_summary">
                Behavior data summary
              </label>
              <textarea
                id="behavior_data_summary"
                className="input mt-1 min-h-[70px]"
                disabled={!editable}
                {...register('behavior_data_summary')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" disabled={!editable} {...register('supervision_provided')} />
                  Supervision provided
                </label>
              </div>
              <div>
                <label className="label" htmlFor="supervision_type">
                  Supervision type
                </label>
                <select id="supervision_type" className="input mt-1" disabled={!editable} {...register('supervision_type')}>
                  <option value="">None</option>
                  <option value="direct">Direct observation</option>
                  <option value="indirect">Indirect/consultation</option>
                  <option value="concurrent">Concurrent supervision</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label" htmlFor="supervision_notes">
                  Supervision notes
                </label>
                <textarea
                  id="supervision_notes"
                  className="input mt-1 min-h-[70px]"
                  disabled={!editable}
                  {...register('supervision_notes')}
                />
              </div>
            </div>

            <div className="text-xs text-gray-500">
              {note?.content_hash ? `Locked content hash: ${note.content_hash}` : 'Note is not locked.'}
            </div>

            {/* BEGIN PWA HARDENING */}
            {!isOnline && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Finalizing (sign/lock) requires an active connection.
              </div>
            )}
            {/* END PWA HARDENING */}
          </form>

          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Addendums</h2>
            </div>

            {note && (note.addendums?.length ?? 0) > 0 ? (
              <div className="space-y-3">
                {note.addendums.map((a) => (
                  <div key={a.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{a.reason}</div>
                        <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{a.content}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-right">
                        <div>{a.created_by_name}</div>
                        <div>{a.created_at}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">Hash: {a.content_hash}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-600">No addendums.</div>
            )}

            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Add addendum</h3>
              <p className="text-xs text-gray-600 mt-1">
                Addendums are appended to preserve immutability. Originals remain unchanged.
              </p>

              <div className="mt-3 grid grid-cols-1 gap-3">
                <div>
                  <label className="label" htmlFor="addendum_reason">
                    Reason
                  </label>
                  <input
                    id="addendum_reason"
                    className="input mt-1"
                    value={addendumReason}
                    onChange={(e) => setAddendumReason(e.target.value)}
                    disabled={!isOnline}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="addendum_content">
                    Content
                  </label>
                  <textarea
                    id="addendum_content"
                    className="input mt-1 min-h-[90px]"
                    value={addendumContent}
                    onChange={(e) => setAddendumContent(e.target.value)}
                    disabled={!isOnline}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={
                    !isOnline ||
                    addendumMutation.isPending ||
                    !addendumReason.trim() ||
                    !addendumContent.trim()
                  }
                  onClick={() => addendumMutation.mutate()}
                >
                  {addendumMutation.isPending ? 'Adding...' : 'Add addendum'}
                </button>
              </div>

              {!isOnline && (
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                  Offline: addendums require connectivity.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow</h2>
            {note ? (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="font-medium text-gray-900">{note.status_display ?? note.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Created by</span>
                  <span className="font-medium text-gray-900">{note.created_by_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Signed</span>
                  <span className="font-medium text-gray-900">{note.signed_at ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Locked</span>
                  <span className="font-medium text-gray-900">{note.locked_at ? 'Yes' : 'No'}</span>
                </div>
                <Link to={`/app/sessions/${note.session}`} className="btn-secondary w-full text-center">
                  Session
                </Link>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No workflow data.</div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Gate</h2>
            {note ? (
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700">
                  Billing requires: completed session, locked note, active authorization, and clinical quality gate pass.
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Billable</span>
                  <span className="font-medium text-gray-900">{note.is_billable ? 'Yes' : 'No'}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No billing data.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}












